'use client';
import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Container,
  Flex,
  Heading,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Tr,
} from '@chakra-ui/react';
import { produce } from 'immer';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';

import CustomImage from '@/app/_components/custom-image/CustomImage';
import { PriceFormat } from '@/app/_components/price-format/price-format';
import { infoTableOptions } from '@/constants/details-car/details-car.constants';
import { duoportaSpecificationNames } from '@/constants/new-cars/new-cars.constants';
import { desktop } from '@/constants/vars.constants';
import { useWindowSize } from '@/hooks/window-size.hook';
import { getImageLink } from '@/services/link.service';
import {
  getFeaturesByDuoportaId,
  getSpecificationsByDuoportaId,
} from '@/services/NewCarsService.service';
import { getVehicleDetailsBanner } from '@/services/pages/pages.service';
import {
  VehicleExtended,
  VehicleInfoTableCell,
} from '@/types/details-car/details-car.types';

import Styles from './VehicleDetails.module.scss';

// TO-DO: Implement proper APIs
const generalDataInfo: VehicleInfoTableCell[] = [
  {
    fullName: 'Make',
    name: 'brand',
  },
  {
    fullName: 'Model',
    name: 'range_id',
  },
  {
    fullName: 'Year',
    name: 'year',
  },
  {
    fullName: 'Mileage',
    name: 'mileage',
    format: (value) => (<PriceFormat value={value} suffix="km" />) as any,
  },
  {
    fullName: 'VIN',
    name: 'vin',
  },
  {
    fullName: 'Registration',
    name: 'registrationNumber',
  },
  {
    fullName: 'Year Registered',
    name: 'registrationDate',
    format: (value) => value && value.split('-')[0],
  },
];

const bodyDataInfo: VehicleInfoTableCell[] = [
  {
    fullName: 'Body type',
    name: duoportaSpecificationNames.bodyShape,
  },
  {
    fullName: 'Doors',
    name: duoportaSpecificationNames.doors,
  },
  {
    fullName: 'Length',
    name: duoportaSpecificationNames.length,
  },
  {
    fullName: 'Width',
    name: duoportaSpecificationNames.widthExclMirrorsInclMirrors,
  },
  {
    fullName: 'Height',
    name: duoportaSpecificationNames.height,
  },
  {
    fullName: 'Weight',
    name: duoportaSpecificationNames.grossWeightGvm,
  },
  {
    fullName: 'Seats',
    name: duoportaSpecificationNames.seatsQuantity,
  },
  {
    fullName: 'Front tyres',
    name: duoportaSpecificationNames.frontTyres,
  },
  {
    fullName: 'Rear tyres',
    name: duoportaSpecificationNames.rearTyres,
  },
  {
    fullName: 'Driven wheels',
    name: duoportaSpecificationNames.drivenWheels,
  },
];

const fuelDataInfo: VehicleInfoTableCell[] = [
  {
    fullName: 'Fuel type',
    name: duoportaSpecificationNames.fuelType,
  },
  {
    fullName: 'Fuel average (100km)',
    name: duoportaSpecificationNames.fuelConsumptionAverage,
  },
  {
    fullName: 'Fuel tank capacity (L)',
    name: duoportaSpecificationNames.fuelTankCapacityInclReserve,
  },
  {
    fullName: 'Fuel range (km)',
    name: duoportaSpecificationNames.fuelRangeAverage,
  },
];

const engineDataInfo: VehicleInfoTableCell[] = [
  {
    fullName: 'Engine',
    name: duoportaSpecificationNames.engineDetail,
  },
  {
    fullName: 'Engine position',
    name: duoportaSpecificationNames.enginePositionLocation,
  },
  {
    fullName: 'Cylinders',
    name: duoportaSpecificationNames.cylinders,
  },
  {
    fullName: 'Power (kW)',
    name: duoportaSpecificationNames.powerMaximum,
  },
  {
    fullName: 'Torque (Nm)',
    name: duoportaSpecificationNames.torqueMaximum,
  },
  {
    fullName: 'Gearshift',
    name: duoportaSpecificationNames.gearshift,
  },
  {
    fullName: 'Drivetrain',
    name: duoportaSpecificationNames.transmissionType,
  },
  {
    fullName: 'CO2 emissions (average) g/km',
    name: duoportaSpecificationNames.co2EmissionsAverage,
  },
  {
    fullName: 'Acceleration 0-100 km/h',
    name: duoportaSpecificationNames.acceleration0100KmH,
  },
  {
    fullName: 'Maximum/top speed km/h',
    name: duoportaSpecificationNames.maximumTopSpeed,
  },
];

const VehicleDetails = ({ vehicleInfo }: { vehicleInfo: VehicleExtended }) => {
  const size = useWindowSize();
  const [sectionOptions, setSectionOptions] = useState(infoTableOptions);

  const [vehicleSpecificationInfo, setVehicleSpecificationInfo] =
    useState<any>(null);
  const [vehicleFeaturesInfo, setVehicleFeaturesInfo] = useState<any>(null);
  const [tableData, setTableData] = useState<any>(null);
  const [selectedExpandMenu, setSelectedExpandMenu] = useState<number>(-1);

  const [pageInfo, setPageInfo] = useState({
    image: '',
    title: '',
    link: '',
    url: '',
  });

  useEffect(() => {
    (async function () {
      try {
        const content = await getVehicleDetailsBanner();

        setPageInfo(content);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    if (vehicleInfo?.duoportaId) {
      getSpecificationsByDuoportaId(vehicleInfo?.duoportaId).then(
        (specifications) => {
          setVehicleSpecificationInfo(specifications);
        },
      );
      getFeaturesByDuoportaId(vehicleInfo?.duoportaId).then((features) => {
        setVehicleFeaturesInfo(features);
      });
    }
  }, [vehicleInfo]);

  const getCellContent = (cell: VehicleInfoTableCell, vehicleInfo: any) => ({
    key: cell.fullName,
    specName: cell.fullName,
    specValue: cell.format
      ? cell.format(vehicleInfo[cell.name])
      : vehicleInfo[cell.name],
  });

  const getSpecifications = (cell: any, vehicleSpecifications: any) => ({
    key: cell.fullName,
    specName: cell.fullName,
    specValue: cell.format
      ? cell.format(
          vehicleSpecifications
            ?.find((specification: any) => specification.name === cell.name)
            ?.value?.toUpperCase(),
        )
      : vehicleSpecifications
          ?.find((specification: any) => specification.name === cell.name)
          ?.value?.toUpperCase(),
  });

  const capitalizeFirstLetter = (line: string): string =>
    line.charAt(0).toUpperCase() + line.slice(1);

  const getTableContent = (optionId: number) => {
    switch (optionId) {
      case 1:
        return generalDataInfo.map((cell) => getCellContent(cell, vehicleInfo));
      case 2:
        return vehicleSpecificationInfo
          ? bodyDataInfo.map((cell) =>
              getSpecifications(cell, vehicleSpecificationInfo),
            )
          : [];
      case 3:
        return vehicleSpecificationInfo
          ? fuelDataInfo.map((cell) =>
              getSpecifications(cell, vehicleSpecificationInfo),
            )
          : [];
      case 4:
        return vehicleSpecificationInfo
          ? engineDataInfo.map((cell) =>
              getSpecifications(cell, vehicleSpecificationInfo),
            )
          : [];
      case 5:
        return vehicleFeaturesInfo
          ? vehicleFeaturesInfo.map((feature: any) => ({
              key: feature.name,
              specName: capitalizeFirstLetter(feature.name),
              specValue: feature.value?.toUpperCase(),
            }))
          : [];
      default:
        return [];
    }
  };

  useEffect(() => {
    const selectedSectionOption = sectionOptions.find(
      (option) => option.isSelected,
    );

    const tableData = getTableContent(selectedSectionOption?.id || 0);

    setTableData(tableData);
  }, [sectionOptions]);

  useEffect(() => {
    const initialTableContent = getTableContent(1);

    setTableData(initialTableContent);
  }, [vehicleInfo]);

  const toggleVehicleDetailsSection = (newSectionOption: any) => {
    const updatedSectionOptions = produce(
      sectionOptions,
      (sectionOptionsDraft) => {
        const selectedSectionOption = sectionOptionsDraft.find(
          (option) => option.isSelected,
        );
        const newSelectedSectionOption = sectionOptionsDraft.find(
          (option) => option.id === newSectionOption.id,
        );

        if (!selectedSectionOption || !newSelectedSectionOption) return;
        selectedSectionOption.isSelected = false;
        newSelectedSectionOption.isSelected = true;
      },
    );

    setSectionOptions(updatedSectionOptions);
  };

  function DataTabs({
    data,
  }: {
    data: { specName: string; specValue: string }[];
  }) {
    return (
      <Tabs className={Styles.vehicleDetailsTab} variant="unstyled">
        <TabList className={Styles.vehicleDetailsTabList}>
          {sectionOptions.map((tab, index) => (
            <Tab
              key={index}
              className={`${Styles.vehicleDetailsTabItem} ${tab.isSelected ? Styles.active : ''}`}
              onClick={() => toggleVehicleDetailsSection(tab)}
            >
              {tab.name}
            </Tab>
          ))}
        </TabList>
        <TabPanels className={Styles.vehicleDetailsTabPanels}>
          {sectionOptions.map((tab, index) => (
            <TabPanel key={index} className={Styles.vehicleDetailsTabPanel}>
              <Table className={Styles.vehicleDetailsTable}>
                <Tbody>
                  {data?.map((content, index) => (
                    <Tr key={index}>
                      <Td className={Styles.specName}>{content.specName}</Td>
                      <Td className={Styles.specValue}>{content.specValue}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    );
  }

  function DataAccordion({
    data,
  }: {
    data: { specName: string; specValue: string }[];
  }) {
    return (
      <Accordion
        allowToggle
        className={Styles.vehicleDetailsAccordion}
        index={selectedExpandMenu}
        onChange={(index) => setSelectedExpandMenu(index as number)}
      >
        {sectionOptions.map((tab, index) => (
          <AccordionItem
            key={index}
            className={Styles.vehicleDetailsAccordionItem}
            onClick={() => toggleVehicleDetailsSection(tab)}
          >
            <h2>
              <AccordionButton className={Styles.vehicleDetailsAccordionButton}>
                <Box as="span" flex="1" textAlign="left">
                  {tab.name}
                </Box>
                <AccordionIcon className={Styles.vehicleDetailsAccordionIcon} />
              </AccordionButton>
            </h2>
            <AccordionPanel className={Styles.vehicleDetailsAccordionPanel}>
              <Table className={Styles.vehicleDetailsTable}>
                <Tbody>
                  {data?.map((content, index) => (
                    <Tr key={index}>
                      <Td className={Styles.specName}>{content.specName}</Td>
                      <Td className={Styles.specValue}>{content.specValue}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    );
  }
  return (
    <Container
      maxW={'1280'}
      width={'100%'}
      className={Styles.vehicleDetailsWrapper}
      paddingY={{ base: 10, lg: 20 }}
      paddingX={{ base: 6, lg: 4 }}
    >
      <Box className={Styles.headingElement} marginBottom={{ base: 10, md: 4 }}>
        <Text fontSize={'xs'} marginBottom={4}>
          Vehicle Details
        </Text>
        <Heading as="h2" className={Styles.titleLg} marginBottom={4}>
          What will you get?
        </Heading>
      </Box>
      <Flex gap={8} width={'100%'}>
        {size?.width < desktop ? (
          <DataAccordion data={tableData} />
        ) : (
          <DataTabs data={tableData} />
        )}

        <Box className={Styles.vehicleDetailsPromo}>
          <Box className={Styles.promoImage}>
            <CustomImage
              imageSrc={getImageLink(pageInfo.image ?? '') as string}
              width={386}
              height={253}
              imageKeys={{
                desktopKey: 'image-386-270',
                mobileKey: 'image-386-270',
              }}
            />
          </Box>
          <Box className={Styles.promoWrapper}>
            <Box className={Styles.promoText}>
              <Box className={Styles.promoTitle}>{pageInfo.title}</Box>
              <Link href={pageInfo.url} className={Styles.promoLink}>
                <ChevronRightIcon /> {pageInfo.link}
              </Link>
            </Box>
          </Box>
        </Box>
      </Flex>
    </Container>
  );
};

export default VehicleDetails;
